/**
 * Server-state hook for the agent list.
 *
 * Replaces the legacy `useEffect(setTimeout(loadAgents, 100), [isAuthenticated])`
 * pattern in AgentContext: `enabled: !!user` is the React Query idiom for
 * "wait until auth is ready", removing the 100 ms race-guard.
 *
 * The query key is namespaced by user.id so account-switches on the same
 * tab invalidate the previous user's cache (mirrors useConversations /
 * useFolders).
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { agentService } from '@/services/agentService';
import { useAuth } from '@/context/AuthContext';
import type { Agent } from '@/types/agent.types';

export const AGENTS_QUERY_KEY = ['agents'] as const;

async function fetchAgents(): Promise<Agent[]> {
  // Phase 3 B-9: pull the heavy shape because multiple list-iterating
  // callsites read heavy fields (config / capabilities / systemPrompt) off
  // each row. Mirrors the legacy AgentContext.loadAgents() call.
  await agentService.initialize({ includeConfig: true });
  return agentService.getAllAgents();
}

export function useAgentsQuery(): UseQueryResult<Agent[], Error> {
  const { user } = useAuth();
  return useQuery<Agent[], Error>({
    // Keep ['agents'] as the prefix so invalidation by prefix matches
    // either ['agents'] or ['agents', userId]. The user.id suffix scopes
    // the cache to the signed-in identity.
    queryKey: [...AGENTS_QUERY_KEY, user?.id ?? null],
    queryFn: fetchAgents,
    enabled: !!user,
    // Agents change rarely — admin edits will invalidate explicitly via
    // mutation onSuccess. 5 minutes mirrors AdminContext.
    staleTime: 5 * 60 * 1000,
  });
}
